import { NextPlan, Outbound, RemoveRedEye } from "@mui/icons-material";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import { debounce } from "lodash";
import moment from "moment";
import MUIDataTable from "mui-datatables";
import { Fragment, useEffect, useMemo, useState } from "react";

import { getAllOrders } from "../../../../apis/cart/apis";
import { CreateOrderStatusHistory } from "../../../../apis/cart/Order/OrderStatusHistory";
import { OrderStatusForm } from "./OrderStatusForm";
import { PriceCard } from "./PriceCard";
import { ProductList } from "./ProductList";

import { Cancel, LocalShipping } from "@mui/icons-material";
import {
	IconCircleDashedCheck,
	IconLoader2,
	IconShoppingCartCheck,
} from "@tabler/icons-react";
import { showToast } from "../../../../utils/toast";

const statusMeta = {
	Received: {
		color: "#9E9E9E",
		icon: <IconShoppingCartCheck color="#9E9E9E" />,
	},
	Processing: {
		color: "#42A5F5",
		icon: (
			<Box
				sx={{
					display: "inline-flex",
					animation: "spin 1s linear infinite",
					"@keyframes spin": {
						to: { transform: "rotate(360deg)" },
					},
				}}
			>
				<IconLoader2 color="#42A5F5" />
			</Box>
		),
	},
	Cancelled: {
		color: "#EF5350",
		icon: <Cancel fontSize="small" color="#EF5350" />,
	},
	Confirmed: {
		color: "#FFA726",
		icon: <IconCircleDashedCheck fontSize="small" color="#FFA726" />,
	},
	Fulfilled: {
		color: "#66BB6A",
		icon: <LocalShipping fontSize="small" />,
	},
};

export const OrderList = ({ status, onStatusChange, searchOpen = false }) => {
	const [data, setData] = useState([]);
	const [order, setOrder] = useState(null);
	const [page, setPage] = useState(0);
	const [filters, setFilters] = useState({});
	const [rowsCount, setRowsCount] = useState(10);
	const [count, setCount] = useState(0);
	const [stage, setStage] = useState("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [message, setMessage] = useState("");
	const [refresh, setRefresh] = useState(false);

	useEffect(() => {
		const searchText = filters?.search ?? "";

		if (searchOpen && searchText === "") {
			return;
		}
		const updatedFilters = status ? { ...filters, status } : filters;
		getAllOrders(rowsCount, page + 1, updatedFilters).then((res) => {
			setData(res.results);
			setCount(res.count);
		});
	}, [page, rowsCount, filters, status, searchOpen]);

	const handleClickOpen = (order, setOpen) => {
		setOpen(true);
		if (order.status.toLowerCase() === "received") {
			setStage("processing");
		} else if (order.status.toLowerCase()) {
			setStage("initial");
		}
		setOrder(order);
	};

	const handleProcessingButton = async (order) => {
		try {
			const formdata = {
				old_status: order.status,
				new_status: "Processing",
			};
			const res = await CreateOrderStatusHistory(order.id, formdata);
			if (res.success) {
				if (res.exists) {
					setMessage(res.message);
					setDialogOpen(true);
					setRefresh(true);
				} else {
					setData((prev) => prev.filter(({ id }) => id !== order.id));
					showToast(
						"Order moved to Processing tab successfully!",
						"success",
						5000
					);
					onStatusChange?.();
					setDialogOpen(false);
				}
			} else {
				showToast(
					"Failed to update order status. Please try again.",
					"error"
				);
				setDialogOpen(false);
			}
		} catch (error) {
			console.error("Failed to update order status:", error);
			showToast(
				"Failed to update order status. Please try again.",
				"error"
			);
			setDialogOpen(false);
		} finally {
			setOrder(null);
		}
	};

	const columns = [
		{
			name: "number",
			label: "order id",
			options: {
				filter: false,
				sort: true,
				setCellProps: () => ({ sx: { textAlign: "right" } }),
				// customBodyRender: (value, tableMeta, updateValue) => {
				// 	// console.log("yes", tableMeta);
				// 	return tableMeta.rowIndex + 1;
				// },
			},
		},
		{
			name: "owner",
			label: "Customer",
			options: {
				filter: false,
				sort: true,
				// setCellProps: () => ({ sx: { textAlign: "right" } }),
				// customBodyRender: (value, tableMeta, updateValue) => {
				// 	// console.log("yes", tableMeta);
				// 	return tableMeta.rowIndex + 1;
				// },
				customBodyRender: (value) => {
					return (
						<Box
							sx={{
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
							}}
						>
							<Typography variant="p" component={"p"}>
								{value.name}
							</Typography>

							{/* <CopyToClipboard
								text={value}
								onCopy={() => notify("copied")}
							>
								<IconButton size="small" color="primary">
									<ContentCopyRounded />
								</IconButton>
							</CopyToClipboard> */}
						</Box>
					);
				},
			},
		},

		{
			name: "lines",
			label: "Products",
			options: {
				filter: false,
				sort: true,

				customBodyRender: (value, tableMeta, updateValue) => {
					return <ProductList products={value} />;
				},
			},
		},
		{
			name: "date_placed",
			label: "Order Placed",
			options: {
				filter: false,
				sort: true,
				customBodyRender: (value, tableMeta, updateValue) => {
					return (
						<>
							<Typography
								variant={"p"}
								component={"p"}
							>{`${moment(value).format(
								"MMM DD YYYY hh:mm A"
							)}`}</Typography>
						</>
					);
				},
			},
		},

		{
			name: "price Details",
			label: "price Details",
			options: {
				filter: false,
				sort: true,
				customBodyRender: (value, tableMeta, updateValue) => {
					const row = data[tableMeta.rowIndex];
					return <PriceCard row={row} />;
				},
			},
		},
		{
			name: "status_history",
			label: "Staff",
			options: {
				filter: false,
				sort: false,
				customBodyRender: (value, tableMeta, updateValue) => {
					return value?.length
						? value
							.filter(
								({ content_type }) =>
									content_type !== "line"
							)
							.map(({ new_status, changed_by }, i) => (
								<Fragment key={`staff-${i}`}>
									<Typography
										variant="body2"
										component="p"
									>
										{`${new_status} - ${changed_by.name}`}
									</Typography>
								</Fragment>
							))
						: "-";
				},
			},
		},
		{
			name: "status",
			label: "status",
			options: {
				filter: false,
				sort: true,
				customBodyRender: (value, tableMeta, updateValue) => {
					return (
						<Chip
							label={value}
							variant="outlined"
							// size="small"
							icon={statusMeta[value].icon}
							sx={{
								borderColor: statusMeta[value].color,
								color: statusMeta[value].color,
							}}
						/>
					);
				},
			},
		},

		{
			name: "",
			label: "Action",
			options: {
				filter: false,
				sort: true,
				viewColumns: false,
				customBodyRender: (value, tableMeta, updateValue) => {
					const row = data[tableMeta.rowIndex];
					return (
						<Box>
							<IconButton
								color="primary"
								href={`/w86zjruk6q/cart/orders/${row.id}/detail`}
							>
								<RemoveRedEye />
							</IconButton>
							{/* <IconButton
								color="warning"
								onClick={() => {
									handleClickOpen(row, setDialogOpen);
								}}
							>
								<NextPlan />
							</IconButton> */}
							{row.status.toLowerCase() === "received" ? (
								<Tooltip title="Update Order to Processing">
									<IconButton
										color="warning"
										onClick={() => {
											handleClickOpen(row, setDialogOpen);
										}}
									>
										<NextPlan />
									</IconButton>
								</Tooltip>
							) : row.status.toLowerCase() === "processing" ? (
								<IconButton
									color="warning"
									onClick={() => {
										handleClickOpen(row, setOpen);
									}}
								>
									<Outbound />
								</IconButton>
							) : null}
						</Box>
					);
				},
			},
		},
	];

	const handleSearchChange = useMemo(
		() => debounce((searchText) => {
			setFilters((prev) => ({
				...prev,
				search: searchText,
			}));
			setData([]);
		}, 500),
		[]
	);

	const options = {
		searchAlwaysOpen: searchOpen,
		textLabels: {
			body: {
				noMatch: "No Orders found",
			},
		},
		jumpToPage: true,
		selectableRows: "none",
		download: false,
		filterType: "multiselect",
		filter: false,
		serverSide: true,
		print: false,
		count: count,
		page: page,
		rowsPerPage: rowsCount,
		rowsPerPageOptions: [10, 25, 100],
		searchPlaceholder: "Search orders by Order ID or Customer Name",
		onChangePage(currentPage) {
			setPage(currentPage);
		},
		onChangeRowsPerPage(numberOfRows) {
			setRowsCount(numberOfRows);
		},
		onSearchChange: handleSearchChange,
	};

	useEffect(() => {
		setMessage(
			dialogOpen
				? "Are you Sure Do you want to Update OrderStatus from Received to Processing"
				: ""
		);
	}, [dialogOpen]);

	const [open, setOpen] = useState(false);
	const handleClose = () => {
		setData((prev) => prev.filter(({ id }) => id !== order.id));
		setOpen(false);
		setOrder(null);
		onStatusChange?.();
	};

	return (
		<Box>
			<MUIDataTable
				title={"Orders"}
				data={data}
				columns={columns}
				options={options}
			/>
			<OrderStatusForm
				order={order}
				open={open}
				stage={stage}
				handleClose={handleClose}
			/>

			<Dialog
				open={dialogOpen}
				onClose={(event, reason) => {
					if (
						reason !== "backdropClick" &&
						reason !== "escapeKeyDown"
					) {
						setDialogOpen(false);
					}
				}}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">{"Warning"}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{message}
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2 }}>
					{refresh ? (
						<Button
							onClick={() => window.location.reload()}
							variant="contained"
						>
							Refresh to update Orders
						</Button>
					) : (
						<>
							<Button
								onClick={() => setDialogOpen(false)}
								variant="contained"
								color="error"
							>
								No
							</Button>
							<Button
								onClick={() => handleProcessingButton(order)}
								variant="contained"
							>
								Yes, Update
							</Button>
						</>
					)}
				</DialogActions>
			</Dialog>
		</Box>
	);
};
